import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../contexts/GameContext";
import { useCoop } from "../contexts/CoopContext";
import { CLASSES } from "../data/classes";
import { Skull, Trophy, RotateCcw, Home, Sword, Shield, Zap, Sparkles } from "lucide-react";

const GameOver = () => {
  const { character, floor, meta, handleDeath, coopMode, gameLoaded } = useGame();
  const coop = useCoop();
  const navigate = useNavigate();
  // Capture snapshot ONLY when the character is actually loaded; before handleDeath nukes it.
  const [snapshot, setSnapshot] = useState(null);
  const [deathHandled, setDeathHandled] = useState(false);

  useEffect(() => {
    if (!gameLoaded || deathHandled) return;
    if (character) {
      setSnapshot({
        finalFloor: character.floor || floor,
        finalGold: character.gold || 0,
        finalPassifs: character.passifs?.length || 0,
        classeId: character.classeId || null,
        classeName: character.nom || "Aventurier",
        runStats: character.runStats || null,
      });
      if (!coopMode) handleDeath();
    } else {
      // No active character — likely reached via "View previous run" or refresh
      setSnapshot({
        finalFloor: floor,
        finalGold: 0,
        finalPassifs: 0,
        classeId: meta?.lastDeath?.classeId || null,
        classeName: "Aventurier",
        runStats: null,
      });
    }
    setDeathHandled(true);
    // eslint-disable-next-line
  }, [gameLoaded, character]);

  const rs = snapshot?.runStats || {};
  const topSkills = Object.entries(rs.damageBySkill || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  const totalSkillDmg = topSkills.reduce((s, [, v]) => s + v, 0);

  if (!snapshot) return null;

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots flex flex-col items-center justify-center p-4 overflow-y-auto relative">
      <div className="absolute inset-0 bg-gradient-to-b from-rose-950/50 to-slate-900 pointer-events-none" />

      <div className="relative z-10 max-w-xl w-full text-center my-4">
        <Skull className="w-20 h-20 mx-auto text-rose-500 mb-3 animate-float" />
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-2" style={{ textShadow: "4px 4px 0 #0f172a" }}>
          GAME OVER
        </h1>
        <p className="text-slate-300 mb-3 text-sm">Vous avez péri dans les profondeurs du donjon.</p>
        {coopMode ? (
          <p className="text-amber-400 font-bold text-sm mb-4 bg-amber-500/10 border-2 border-amber-500 rounded-xl px-3 py-2">
            ⚠️ Mode Co-op : les runs sont uniques, les passifs ne sont pas conservés.
          </p>
        ) : snapshot.finalPassifs > 0 && (
          <p data-testid="passifs-info-msg" className="text-slate-400 text-xs mb-4 italic">
            {snapshot.finalPassifs} passif{snapshot.finalPassifs > 1 ? "s collectés ont" : " collecté a"} été perdu{snapshot.finalPassifs > 1 ? "s" : ""} avec ta mort. Une nouvelle run = une nouvelle page blanche.
          </p>
        )}

        <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-4 cartoon-shadow-lg-static mb-4 space-y-2 text-left">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-xs uppercase tracking-wider font-bold">Classe</span>
            <span className="font-display font-bold">{snapshot.classeName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-xs uppercase tracking-wider font-bold flex items-center gap-1"><Trophy className="w-4 h-4 text-yellow-400" /> Étage atteint</span>
            <span data-testid="final-floor" className="font-display font-bold text-yellow-400">{snapshot.finalFloor}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-xs uppercase tracking-wider font-bold">Or possédé</span>
            <span className="font-display font-bold">{snapshot.finalGold}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-xs uppercase tracking-wider font-bold">Passifs collectés</span>
            <span className="font-display font-bold text-fuchsia-400">{snapshot.finalPassifs}</span>
          </div>
          {!coopMode && (
            <div className="border-t border-slate-700 pt-2 flex items-center justify-between">
              <span className="text-slate-400 text-xs uppercase tracking-wider font-bold">Record d'étage</span>
              <span className="font-display font-bold text-yellow-400">{meta.highestFloor}</span>
            </div>
          )}
        </div>

        {/* Damage / Run summary */}
        {snapshot.runStats && (
          <div data-testid="run-summary" className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-4 cartoon-shadow-lg-static mb-4 text-left">
            <h2 className="font-display font-bold text-center text-base mb-3 text-rose-400 uppercase tracking-wider">📊 Détail des dégâts</h2>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-rose-500 border-2 border-slate-950 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="font-bold text-sm flex items-center gap-1"><Sword className="w-4 h-4" /> Infligés</span>
                <span data-testid="total-dmg-dealt" className="font-display font-bold">{rs.totalDamageDealt || 0}</span>
              </div>
              <div className="bg-sky-500 border-2 border-slate-950 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="font-bold text-sm flex items-center gap-1"><Shield className="w-4 h-4" /> Subis</span>
                <span data-testid="total-dmg-taken" className="font-display font-bold">{rs.totalDamageTaken || 0}</span>
              </div>
              <div className="bg-orange-500 border-2 border-slate-950 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="font-bold text-sm flex items-center gap-1"><Zap className="w-4 h-4" /> Max coup</span>
                <span className="font-display font-bold">{rs.maxHit || 0}</span>
              </div>
              <div className="bg-pink-500 border-2 border-slate-950 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="font-bold text-sm flex items-center gap-1"><Sparkles className="w-4 h-4" /> Crits</span>
                <span className="font-display font-bold">{rs.criticalsLanded || 0}</span>
              </div>
              <div className="bg-emerald-500 border-2 border-slate-950 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="font-bold text-sm">💀 Ennemis</span>
                <span className="font-display font-bold">{rs.enemiesDefeated || 0}</span>
              </div>
              <div className="bg-purple-500 border-2 border-slate-950 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="font-bold text-sm">👑 Boss</span>
                <span className="font-display font-bold">{rs.bossesDefeated || 0}</span>
              </div>
            </div>
            {topSkills.length > 0 && (
              <div>
                <p className="font-display font-bold text-xs uppercase tracking-wider text-slate-400 mb-1">Top compétences</p>
                <div className="space-y-1">
                  {topSkills.map(([name, dmg]) => {
                    const pct = totalSkillDmg > 0 ? (dmg / totalSkillDmg) * 100 : 0;
                    return (
                      <div key={name} className="bg-slate-900 border-2 border-slate-700 rounded-lg p-1.5">
                        <div className="flex items-center justify-between text-xs font-bold mb-0.5">
                          <span className="text-slate-100">{name}</span>
                          <span className="text-yellow-400">{dmg}</span>
                        </div>
                        <div className="h-1.5 bg-slate-950 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-rose-500 to-orange-400" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {coopMode ? (
            <button
              data-testid="back-lobby-after-death"
              onClick={() => { if (coop?.isHost) coop.endRun(); navigate("/lobby"); }}
              className="flex-1 bg-fuchsia-500 text-white border-4 border-slate-950 rounded-2xl py-3 font-display font-bold uppercase tracking-wider cartoon-shadow flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" /> Retour au lobby
            </button>
          ) : (
            <button
              data-testid="new-run-after-death"
              onClick={() => navigate("/select")}
              className="flex-1 bg-sky-400 text-slate-950 border-4 border-slate-950 rounded-2xl py-3 font-display font-bold uppercase tracking-wider cartoon-shadow flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" /> Nouvelle aventure
            </button>
          )}
          <button
            data-testid="back-home-after-death"
            onClick={() => navigate("/")}
            className="flex-1 bg-slate-700 text-white border-4 border-slate-950 rounded-2xl py-3 font-display font-bold uppercase tracking-wider cartoon-shadow flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" /> Menu
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameOver;
